import { Component } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import Actions from '../../../../actions'
import Operations from '../../../../operations'
import Selectors from '../../../../selectors'
import { find } from 'lodash'
import RiskAssessmentsSections from './components/RiskAssessmentsSections.tsx'
import { RiskAssessmentPaywall } from './components/RiskAssessmentPaywall'
import { HelpLink, Spinner, SearchSelect } from '../../../../components/generic'
import { sizes } from '@lmc/common/constants'
import { getNominative } from '../../../../common/utils'
import { withRouter } from '../../../../withRouter'
import { history } from '../../../../store'

import {
  Button,
  EmptyState,
  NoDataIcon,
  NotStartedIcon,
  PageHeader
} from '@lmc/ui-kit'
import { isBrowser, isTablet } from 'react-device-detect'
import { ProTag } from 'components/ProTag'

const isDesktop = isBrowser || isTablet

export class ResidentRiskAssessmentDashboardScreen extends Component {
  state = {
    loading: null
  }

  componentDidMount() {
    const {
      router: { params },
      setSortType
    } = this.props
    this.loadData(params)
    setSortType('high-low')
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (this.props.router.params !== nextProps.router.params) {
      this.setState({ loading: true })
      this.loadData(nextProps.router.params)
    }
    if (this.props.riskAssessments !== nextProps.riskAssessments) {
      this.setState({ loading: false })
    }
  }

  loadData = (params) => {
    const query = { resident: params.id }
    if (params.risk_assessment_id) {
      query.riskAssessment = params.risk_assessment_id
    } else {
      query.mostRecent = true
    }
    this.props.loadList('risk-assessments', query)
  }

  getTitle = () => {
    const {
      router: { params },
      resident,
      riskAssessments
    } = this.props

    if (params.risk_assessment_id) {
      const assessment = find(riskAssessments, {
        id: params.risk_assessment_id
      })
      return assessment ? `History - ${assessment.form.name}` : 'History'
    }
    const name = `${resident.name.first} ${resident.name.last}`
    return `${name}'s Risk Assessments`
  }

  handleRiskAssessmentClick = (id) => {
    const {
      router: { params },
      hasAccess
    } = this.props

    const hasRolePermission = hasAccess(
      ['carehome-admin', 'senior-carer'],
      [
        {
          i: 'view',
          this: 'RiskAssessment'
        }
      ]
    )

    if (!hasRolePermission) {
      return
    }

    let url = `/residents/${params.id}/risk-assessments/${id}`
    if (params.risk_assessment_id) {
      url = `/residents/${params.id}/risk-assessments/${params.risk_assessment_id}/history/${id}`
    }

    history.push(url)
  }

  renderInitialAssessmentInfo = (resident) => (
    <EmptyState
      title={`Finalise ${resident.name.first}'s Initial Assessment to start adding Risk Assessments`}
      icon={<NotStartedIcon />}
      data-testid="risk-assessment-initial-assessment-info"
    >
      {getInitialAssessmentInfo(
        this.props.carePlanNominative,
        this.props.preferredPeopleNominative
      )}
      <a
        href={PRE_ASSESSMENT_ARTICLE}
        rel="noreferrer"
        target="_blank"
        data-testid="risk-assessment-pre-assessment-link"
      >
        Find out more
      </a>
    </EmptyState>
  )

  renderEmptyPage = () => (
    <EmptyState
      icon={<NoDataIcon />}
      title="Shoot... No data to show you"
      description="Press that blue button at the top right of the page."
      data-testid="risk-assessment-empty-state"
    >
      <a
        href={EMPTY_DASHBOARD_ARTICLE}
        rel="noreferrer"
        target="_blank"
        data-testid="risk-assessment-empty-dashboard-link"
      >
        Find out more
      </a>
    </EmptyState>
  )

  renderDropdown = () => {
    const {
      router: { params },
      riskAssessments
    } = this.props
    if (params.risk_assessment_id) return null

    return (
      <SearchSelect
        getOptionLabel={(option) => option.form.name}
        getOptionValue={(option) => option.riskScore || option.form.name}
        options={riskAssessments}
        placeholder={DROPDOWN_PLACEHOLDER}
        onChange={(option) => this.handleRiskAssessmentClick(option.id)}
        data-testid="risk-assessment-dropdown"
      />
    )
  }

  render() {
    const {
      carers,
      resident,
      riskAssessments,
      hasCarePlans,
      router,
      hasAccess
    } = this.props

    const { params } = router

    if (!hasCarePlans) {
      return (
        <RiskAssessmentPaywall
          preferredPeopleNominative={getNominative(
            this.props.preferredPeopleNominative,
            'service users'
          ).toLocaleLowerCase()}
        />
      )
    }

    if (!carers || !resident || !riskAssessments) {
      return <Spinner />
    }

    const isInitialAssessment = resident.status === 'pre-assessment'

    const isHistoryRiskAssessmentsRoute =
      router?.location?.pathname?.includes('history')

    const hasRolePermission = hasAccess(
      ['carehome-admin', 'senior-carer'],
      [
        {
          i: 'create',
          this: 'RiskAssessment'
        }
      ]
    )

    return (
      <div
        style={styles.container}
        data-testid="risk-assessment-dashboard-screen"
      >
        <PageHeader
          id="risk-assessment-dashboard-title"
          title={this.getTitle()}
          sticky
          onBack={
            !isDesktop
              ? () => history.replace?.(`/residents/${params.id}/profile`)
              : undefined
          }
          data-testid="risk-assessment-dashboard-header"
        >
          <ProTag data-testid="risk-assessment-pro-tag" />
          {!isHistoryRiskAssessmentsRoute && (
            <Button
              variant="primary"
              onClick={() => this.props.openRiskAssessmentModal(undefined)}
              disabledByPermission={!hasRolePermission}
              data-testid="new-risk-assessment-button"
            >
              New risk assessment
            </Button>
          )}
        </PageHeader>
        {riskAssessments.length ? (
          <div data-testid="risk-assessment-content">
            {this.renderDropdown()}
            <RiskAssessmentsSections
              carers={carers}
              sortType={this.props.sortType}
              carePlanNominative={this.props.carePlanNominative}
              params={params}
              riskAssessments={riskAssessments}
              loading={this.state.loading}
              onRiskAssessmentClick={this.handleRiskAssessmentClick}
              resident={resident}
              setSortType={this.props.setSortType}
              testId="risk-assessment"
            />
          </div>
        ) : isInitialAssessment ? (
          this.renderInitialAssessmentInfo(resident)
        ) : (
          this.renderEmptyPage()
        )}
        <HelpLink
          supportLink={EMPTY_DASHBOARD_ARTICLE}
          style={styles.supportLink}
        />
      </div>
    )
  }
}

const DROPDOWN_PLACEHOLDER = 'Search for current and unattached assessments'

const EMPTY_DASHBOARD_ARTICLE =
  'https://support.logmycare.co.uk/en/articles/3371486-risk-assessment-dashboard'

const PRE_ASSESSMENT_ARTICLE =
  'https://support.logmycare.co.uk/en/articles/3376601-how-do-i-finalise-a-initial-assessment'

const getInitialAssessmentInfo = (
  carePlanNominative,
  preferredPeopleNominative
) =>
  `When you finalise the Initial Assessment for this ${getNominative(
    preferredPeopleNominative,
    'resident'
  )}, you’ll be able to add Risk Assessments to their ${getNominative(
    carePlanNominative,
    'Care Plan'
  )}.`

ResidentRiskAssessmentDashboardScreen.propTypes = {
  carers: PropTypes.array,
  loadList: PropTypes.func.isRequired,
  resident: PropTypes.object,
  riskAssessments: PropTypes.array,
  hasCarePlans: PropTypes.bool
}

const styles = {
  container: {
    margin: '0 auto',
    maxWidth: sizes.childScreen,
    padding: '0 0 50px 0'
  },
  headerContainer: {
    marginBottom: 14
  },
  divider: {
    height: 2,
    marginBottom: 18,
    marginTop: 8,
    width: '100%'
  },
  emptyMessage: {
    marginTop: 5
  },
  noAssessments: {
    marginTop: 24,
    marginBottom: 20
  },
  supportLink: {
    display: 'inline-block',
    float: 'left',
    marginTop: 0
  },
  title: {
    marginBottom: 0
  },
  topContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    paddingTop: 30
  }
}

const RISK_ASSESSMENT_MODAL_ID = 'createRiskAssessment'

const mapStateToProps = (state, props) => ({
  carers: state.data.carers,
  resident: find(state.data.residents, { id: props.router.params.id }),
  riskAssessments: Selectors.sortRiskAssessments(state),
  sortType: state.carePlans.riskAssessmentSortType,
  carePlanNominative: state.global.home.preferredCarePlansNominative,
  preferredPeopleNominative: state.global.home.preferredPeopleNominative,
  hasCarePlans: state.global.home.features.carePlans.enabled,
  hasAccess: (roles, abilities) =>
    Selectors.checkRoleAccess(state, { roles, abilities })
})

const mapDispatchToProps = (dispatch) => ({
  loadList: (list, query) => dispatch(Operations.loadList(list, query)),
  setSortType: (type) => dispatch(Actions.setRiskAssessmentSortType(type)),
  openRiskAssessmentModal: (completedRiskAssessment) => {
    dispatch(
      Actions.openModal({
        modalId: RISK_ASSESSMENT_MODAL_ID,
        data: { completedRiskAssessment }
      })
    )
  }
})

export default withRouter(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )(ResidentRiskAssessmentDashboardScreen)
)
